// Loser pays: a snooker game where each frame is paid for by whoever lost it,
// and a frame costs the table time it took. Mirrors frame_tally / session_sides
// in backend/app/services/frames.py so the card can show who owes what without asking the
// server after every frame. Keep the two in sync.
import {
  computeGameCharge, computeSessionCharge, planFromSession, sessionPauses, sessionPlanPhases,
} from "./billing";

export const FRAME_BILLING = "frames";

/** True when this session is charged by the frame. */
export function isFrameGame(session) {
  return session?.billing_mode === FRAME_BILLING;
}

/** Who frames are charged to: a player each, or a team each. */
export function sessionSides(session) {
  if (session?.sides?.length) return session.sides;
  return (session?.players || []).map((p) => ({ id: p.id, name: p.name, member_ids: [p.id] }));
}

/** When the frame now being played began. */
export function lastFrameEnd(session) {
  const frames = session?.frames || [];
  return frames.length ? frames[frames.length - 1].at : session?.start_time;
}

/**
 * What the frame in progress has run up so far — the same engine the server
 * uses when the frame is closed, so the number on screen is the number charged.
 */
export function framesBilledSoFar(session) {
  return (session?.frames || []).reduce((sum, f) => sum + (f.amount || 0), 0);
}

/**
 * What closing a frame now would add: the table's bill up to this moment, less
 * everything the frames already closed have taken. Tapping Lost moves money
 * onto a name, it never creates any — so the night always comes to the time
 * played. Mirrors frame_charge in backend/app/services/frames.py.
 */
export function currentFrameCharge(session, nowMs = Date.now()) {
  if (!isFrameGame(session)) return { amount: 0, minutes: 0, seconds: 0 };
  const began = new Date(lastFrameEnd(session)).getTime();
  const start = new Date(session.start_time).getTime();
  if (!Number.isFinite(began) || !Number.isFinite(start)) {
    return { amount: 0, minutes: 0, seconds: 0 };
  }
  const pauses = sessionPauses(session, nowMs);
  const whole = computeGameCharge(session, nowMs, pauses);
  const window = computeSessionCharge(planFromSession(session), began, nowMs, pauses, sessionPlanPhases(session));
  const seconds = Math.round(window.playedMs / 1000);
  const earlierMinutes = (session.frames || []).reduce((sum, f) => sum + (f.minutes || 0), 0);
  return {
    amount: Math.max(0, whole.totalAmount - framesBilledSoFar(session)),
    minutes: Math.max(0, whole.billedMinutes - earlierMinutes),
    seconds,
  };
}

/**
 * Who settles what: each side owes the frames it lost plus its members' items.
 * Anything bought without a name stays on the table.
 */
export function frameTally(session) {
  const players = new Map((session?.players || []).map((p) => [p.id, p]));
  const itemRows = new Map(
    [...players.values()].map((p) => [p.id, { playerId: p.id, name: p.name, items: [], itemsTotal: 0 }]),
  );

  const tableItems = [];
  let tableItemsTotal = 0;
  (session?.orders || []).forEach((o) => {
    const row = itemRows.get(o.player_id);
    if (row) {
      row.items.push(o);
      row.itemsTotal += o.total_amount || 0;
    } else {
      tableItems.push(o);
      tableItemsTotal += o.total_amount || 0;
    }
  });

  const framesBySide = new Map();
  (session?.frames || []).forEach((f) => {
    const list = framesBySide.get(f.loser_side_id) || [];
    list.push(f);
    framesBySide.set(f.loser_side_id, list);
  });

  const sides = sessionSides(session).map((side) => {
    const lost = framesBySide.get(side.id) || [];
    const framesAmount = lost.reduce((sum, f) => sum + (f.amount || 0), 0);
    const members = (side.member_ids || []).map((id) => itemRows.get(id)).filter(Boolean);
    const itemsTotal = members.reduce((sum, m) => sum + m.itemsTotal, 0);
    return {
      sideId: side.id,
      name: side.name,
      members,
      isTeam: (side.member_ids || []).length > 1,
      frames: lost.length,
      framesMinutes: lost.reduce((sum, f) => sum + (f.minutes || 0), 0),
      framesAmount,
      itemsTotal,
      total: framesAmount + itemsTotal,
    };
  });

  return {
    sides,
    frameCount: (session?.frames || []).length,
    framesTotal: sides.reduce((sum, s) => sum + s.framesAmount, 0),
    tableItems,
    tableItemsTotal,
  };
}
